import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { API_BASE_URL } from '../config/app-settings.tokens';
import {
    LoginRequest,
    LoginResponse,
    RegisterUserDto,
    RegisterResponse,
    User,
    AuthState,
    TokenValidationResponse,
    VerifyEmailRequest,
    VerifyEmailResponse,
    SendResetPasswordEmailRequest,
    SendResetPasswordEmailResponse,
    ValidateResetTokenResponse,
    ResetPasswordRequest,
    ResetPasswordResponse,
    UserTypeResponse,
    UserTypeItem
} from '../models/auth.model';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private apiUrl = `${inject(API_BASE_URL)}`;
    private authStateSubject = new BehaviorSubject<AuthState>({
        isAuthenticated: false,
        currentUser: null,
        accessToken: null,
        refreshToken: null,
        loading: false,
        error: null
    });

    authState$ = this.authStateSubject.asObservable();

    constructor(
        private http: HttpClient,
        private router: Router,
        private translate: TranslateService
    ) {
        this.initializeAuthState();
    }

    /**
     * Initialize auth state from localStorage on app startup
     */
    private initializeAuthState(): void {
        const storedToken = localStorage.getItem('access_token');
        const storedRefreshToken = localStorage.getItem('refresh_token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
            try {
                const user = JSON.parse(storedUser);
                this.authStateSubject.next({
                    isAuthenticated: storedToken ? true : false,
                    currentUser: user,
                    accessToken: storedToken,
                    refreshToken: storedRefreshToken,
                    loading: false,
                    error: null
                });
            } catch (error) {
                this.clearAuthState();
            }
        }
    }

    /**
     * Login user with username and password
     */
    login(credentials: LoginRequest): Observable<User> {
        this.setLoading(true);
        const loginUrl = `${this.apiUrl}/api/v1/auth/login`;

        return this.http.post<LoginResponse>(loginUrl, credentials).pipe(
            tap(response => {
                if (response.success && response.data) {
                    // Check if account is expired
                    if (response.data.isAccountExpired) {
                        this.setLoading(false);
                        const expirationMessage = response.data.expirationMessage || 'Your account access has expired. Please contact administrator.';
                        this.setError(expirationMessage);
                        throw new Error(expirationMessage);
                    }            
                    const user: User = {
                        firstName: response.data.firstName,
                        lastName: response.data.lastName,
                        displayName: response.data.displayName,
                        email: response.data.email,
                        iamUserId: response.data.iamUserId,
                        accessToken: response.data.accessToken,
                        refreshToken: response.data.refreshToken,
                        tokenType: response.data.tokenType,
                        expiresIn: response.data.expiresIn,
                        isAccountExpired: response.data.isAccountExpired,
                        accountExpirationDate: response.data.accountExpirationDate,
                        expirationMessage: response.data.expirationMessage
                    };

                    // Store tokens and user info
                    localStorage.setItem('access_token', response.data.accessToken);
                    localStorage.setItem('refresh_token', response.data.refreshToken);
                    localStorage.setItem('token_type', response.data.tokenType);
                    localStorage.setItem('user', JSON.stringify(user));

                    // Update auth state
                    this.authStateSubject.next({
                        isAuthenticated: response.data.accessToken ? true : false,
                        currentUser: user,
                        accessToken: response.data.accessToken,
                        refreshToken: response.data.refreshToken,
                        loading: false,
                        error: null
                    });

                    this.setLoading(false);
                } else if (!response.success) {
                    // Handle login failure - check isVerified first, then userStatus
                    let errorMessage: string;
                    
                    if (response.data?.isVerified === false) {
                        errorMessage = this.translate.instant('login.emailNotVerified');
                    } else {
                        errorMessage = this.getLoginErrorMessage(response.data?.userStatus || '');
                    }
                    
                    this.setLoading(false);
                    this.setError(errorMessage);
                    throw new Error(errorMessage);
                } else {
                    throw new Error(response.errors?.join(', ') || 'Login failed');
                }
            }),
            map(response => {
                if(response.success && response.data && response.data.isAccountExpired) {
                    throw new Error(response.data.expirationMessage || 'Your account access has expired. Please contact administrator.');
                }
                return {
                    firstName: response.data.firstName,
                    lastName: response.data.lastName,
                    displayName: response.data.displayName,
                    email: response.data.email,
                    iamUserId: response.data.iamUserId,
                    isAccountExpired: response.data.isAccountExpired,
                    accountExpirationDate: response.data.accountExpirationDate,
                    expirationMessage: response.data.expirationMessage
                };
            }),
            catchError(error => {
                this.setLoading(false);
                let errorMessage = this.translate.instant('login.invalidCredentials');
                
                // Handle API error response structure - check isVerified first
                if (error.error?.data?.isVerified === false) {
                    errorMessage = this.translate.instant('login.emailNotVerified');
                } else if (error.error?.data?.userStatus) {
                    errorMessage = this.getLoginErrorMessage(error.error.data.userStatus);
                } else if (error.error?.errors && Array.isArray(error.error.errors) && error.error.errors.length > 0) {
                    errorMessage = error.error.errors.map((e: any) => e.message || e).join(', ');
                } else if (error.error?.message) {
                    errorMessage = error.error.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                this.setError(errorMessage);
                return throwError(() => ({ message: errorMessage, error }));
            })
        );
    }

    /**
     * Get login error message based on user status
     * Messages are translated based on the current language setting
     */
    private getLoginErrorMessage(userStatus: string): string {
        switch (userStatus) {
            case 'Expired':
                return this.translate.instant('login.accountExpired');
            case 'Blocked':
                return this.translate.instant('login.accountBlocked');
            case 'Disabled':
                return this.translate.instant('login.accountDisabled');
            case 'Pending':
                return this.translate.instant('login.accountPending');
            case 'Rejected':
                return this.translate.instant('login.accountRejected');
            default:
                return this.translate.instant('login.invalidCredentials');
        }
    }

    /**
     * Register new user with form data including file upload
     */
    register(registerData: RegisterUserDto): Observable<RegisterResponse> {
        this.setLoading(true);
        const registerUrl = `${this.apiUrl}/api/v1/auth/register`;

        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append('username', registerData.username);
        formData.append('password', registerData.password);
        formData.append('email', registerData.email);
        formData.append('fullName', registerData.fullName);
        if (registerData.phoneNumber) formData.append('phoneNumber', registerData.phoneNumber);
        if (registerData.position) formData.append('position', registerData.position);
        if (registerData.organization) formData.append('organization', registerData.organization);
        if (registerData.justification) formData.append('justification', registerData.justification);
        formData.append('userTypeId', registerData.userTypeId.toString());
        if (registerData.identityAttachment) formData.append('identityAttachment', registerData.identityAttachment);

        return this.http.post<RegisterResponse>(registerUrl, formData).pipe(
            tap(response => {
                this.setLoading(false);
                if (!response.success) {
                    throw new Error(response.errors?.join(', ') || response.message || 'Registration failed');
                }
            }),
            catchError(error => {
                this.setLoading(false);
                const errorMessage = error.error?.errors?.join(', ') || error.error?.message || error.message || 'Registration failed';
                this.setError(errorMessage);
                return throwError(() => ({ message: errorMessage, error }));
            })
        );
    }

    /**
     * Logout user - clear tokens and auth state
     */
    logout(): void {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_type');
        localStorage.removeItem('user');
       
        this.clearAuthState();
        
        // Stop notification connection
        // We need to access the NotificationService, so we'll emit an event
        // The navbar will handle stopping the notification service on logout
        
        this.router.navigate(['/login']);
        
        // Reload page to clear all cached data and state
        setTimeout(() => {
            window.location.reload();
        }, 0);
    }

    /**
     * Refresh access token using refresh token
     */
    refreshAccessToken(): Observable<string> {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
            return throwError(() => new Error('No refresh token available'));
        }

        const refreshUrl = `${this.apiUrl}/api/login/refresh-token`;
        return this.http.post<{ access_token: string; refresh_token: string }>(
            refreshUrl,
            { refresh_token: refreshToken }
        ).pipe(
            tap(response => {
                localStorage.setItem('access_token', response.access_token);
                if (response.refresh_token) {
                    localStorage.setItem('refresh_token', response.refresh_token);
                }
            }),
            map(response => response.access_token),
            catchError(error => {
                this.logout();
                return throwError(() => error);
            })
        );
    }

    /**
     * Get access token
     */
    getAccessToken(): string | null {
        return localStorage.getItem('access_token');
    }

    /**
     * Get refresh token
     */
    getRefreshToken(): string | null {
        return localStorage.getItem('refresh_token');
    }

    /**
     * Get current user
     */
    getCurrentUser(): User | null {
        const state = this.authStateSubject.value;
        return state.currentUser;
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return this.authStateSubject.value.isAuthenticated;
    }

    /**
     * Get current auth state
     */
    getAuthState(): AuthState {
        return this.authStateSubject.value;
    }

    /**
     * Get user by email
     * Used to fetch user details including iamUserId for notifications
     */
    getUserByEmail(email: string): Observable<any> {
        const url = `${this.apiUrl}/api/v1/users/by-email?email=${encodeURIComponent(email)}`;
        return this.http.get<any>(url).pipe(
            catchError(error => {
                console.error('Error fetching user by email:', error);
                return throwError(() => ({
                    message: error?.error?.message || 'Failed to fetch user details',
                    error
                }));
            })
        );
    }


    /**
     * Validate verification token from email link
     */
    validateVerificationToken(token: string): Observable<TokenValidationResponse> {
        const validateUrl = `${this.apiUrl}/api/v1/auth/is-token-valid-and-need-verify`;
        return this.http.get<TokenValidationResponse>(validateUrl, {
            params: { token }
        }).pipe(
            catchError((error: any) => {
                const errorMessage =  error.message || 'Token validation failed';
                return throwError(() => ({ message: errorMessage, error }));
            })
        );
    }

  /**
   * Verify email with OTP code and verification token
   */
  verifyEmail(verifyRequest: VerifyEmailRequest): Observable<VerifyEmailResponse> {
    const verifyUrl = `${this.apiUrl}/api/v1/auth/verify-email`;
    return this.http.post<VerifyEmailResponse>(verifyUrl, verifyRequest).pipe(
      tap(response => {
        if (response.success && response.data.isVerified) {
          // Email verified successfully - user must login separately to get JWT token       
          this.setError(null);
        }
      }),
      catchError((error: any) => {
        const errorMessage = error.error?.message || error.error?.data?.message || error.message || 'Email verification failed';
        this.setError(errorMessage);
        return throwError(() => ({ message: errorMessage, error }));
      })
    );
  }

  /**
   * Send reset password email
   */
  sendResetPasswordEmail(request: SendResetPasswordEmailRequest): Observable<SendResetPasswordEmailResponse> {
    const sendUrl = `${this.apiUrl}/api/v1/auth/send-reset-password-email`;
    return this.http.post<SendResetPasswordEmailResponse>(sendUrl, request).pipe(
      catchError((error: any) => {
        const errorMessage = error.error?.message || error.message || 'Failed to send reset password email';
        this.setError(errorMessage);
        return throwError(() => ({ message: errorMessage, error }));
      })
    );
  }

  /**
   * Validate reset password token
   */
  validateResetToken(token: string): Observable<ValidateResetTokenResponse> {
    const validateUrl = `${this.apiUrl}/api/v1/auth/validate-reset-token/${token}`;
    return this.http.get<ValidateResetTokenResponse>(validateUrl).pipe(
      catchError((error: any) => {
        const errorMessage = error.error?.message || error.message || 'Token validation failed';
        return throwError(() => ({ message: errorMessage, error }));
      })
    );
  }

  /**
   * Reset password with token
   */
  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    const resetUrl = `${this.apiUrl}/api/v1/auth/reset-password`;
    return this.http.post<ResetPasswordResponse>(resetUrl, request).pipe(
      catchError((error: any) => {
        const errorMessage = error.error?.message || error.message || 'Password reset failed';
        this.setError(errorMessage);
        return throwError(() => ({ message: errorMessage, error }));
      })
    );
  }

  /**
   * Private helper methods
   */
    private setLoading(loading: boolean): void {
        const currentState = this.authStateSubject.value;
        this.authStateSubject.next({
            ...currentState,
            loading
        });
    }

    private setError(error: string | null): void {
        const currentState = this.authStateSubject.value;
        this.authStateSubject.next({
            ...currentState,
            error
        });
    }

    /**
     * Clear the error message from auth state
     */
    public clearError(): void {
        this.setError(null);
    }

    /**
     * Fetch available user types for registration
     */
    getUserTypes(): Observable<UserTypeItem[]> {
        const userTypesUrl = `${this.apiUrl}/api/v1/user-types?pageNumber=1&pageSize=10`;
        
        return this.http.get<UserTypeResponse>(userTypesUrl).pipe(
            map(response => {
                if (response.success && response.data.items) {
                    return response.data.items;
                }
                return [];
            }),
            catchError(error => {
                console.error('Failed to fetch user types:', error);
                return throwError(() => ({ message: 'Failed to fetch user types', error }));
            })
        );
    }

    /**
     * Clear auth state - remove tokens and user info
     */
    private clearAuthState(): void {
        this.authStateSubject.next({
            isAuthenticated: false,
            currentUser: null,
            accessToken: null,
            refreshToken: null,
            loading: false,
            error: null
        });
    }
}
